/**
 * Routes utility for consistent navigation across the application
 */
export const routes = {
  // Auth routes
  notFound: "/404",
  login: "/auth/login",
  signup: "/auth/sign-up",
  logout: "/logout",
  verifyEmail: "/verify-email",
  resetPassword: "/reset-password",
  updatePassword: "/update-password",

  // Personal dashboard routes
  personalDashboard: "/dashboard",
  personalChat: "/dashboard/chat",
  personalBrains: "/dashboard/brains",
  personalThreads: "/dashboard/threads",
  personalProjects: "/dashboard/projects",
  personalProject: (id: string) => `/dashboard/projects/${id}`,
  personalProjectFiles: (id: string) => `/dashboard/projects/${id}/files`,
  personalProjectThread: (id: string) => `/dashboard/projects/${id}/threads`,
  personalProjectBrain: (id: string) => `/dashboard/projects/${id}/brains`,
  personalThread: (id: string) => `/dashboard/threads/${id}`,

  // Organization routes
  orgHome: (orgId: string) => `/org/${orgId}`,
  orgSettings: (orgId: string) => `/org/${orgId}/settings`,
  orgSettingsAccount: (orgId: string) => `/org/${orgId}/settings/account`,
  orgSettingsWorkspace: (orgId: string) => `/org/${orgId}/settings/workspace`,
  orgSettingsFeatures: (orgId: string) => `/org/${orgId}/settings/features`,
  orgIntegrations: (orgId: string) => `/org/${orgId}/integrations`,
  orgIntegrationDetail: (orgId: string, appSlug: string) =>
    `/org/${orgId}/integrations/${appSlug}`,
  orgAgents: (orgId: string) => `/org/${orgId}/agents`,
  orgThreads: (orgId: string) => `/org/${orgId}/threads`,
  orgProjects: (orgId: string) => `/org/${orgId}/projects`,
  orgProject: (orgId: string, id: string) => `/org/${orgId}/projects/${id}`,
  orgProjectFiles: (orgId: string, id: string) =>
    `/org/${orgId}/projects/${id}/files`,
  orgProjectThread: (orgId: string, id: string) =>
    `/org/${orgId}/projects/${id}/threads`,
  orgAgent: (orgId: string, agentId: string) =>
    `/org/${orgId}/agents/${agentId}`,
  orgThread: (orgId: string, threadId: string) =>
    `/org/${orgId}/threads/${threadId}`,
  orgProjectFile: (orgId: string, id: string) =>
    `/org/${orgId}/projects/${id}/files`,

  // Data routes - Organization level
  orgDashboards: (orgId: string) => `/org/${orgId}/dashboards`,
  orgDashboardNew: (orgId: string) => `/org/${orgId}/dashboards/new`,
  orgDashboardView: (orgId: string, dashboardId: string) =>
    `/org/${orgId}/dashboards/${dashboardId}`,
  orgDatasets: (orgId: string) => `/org/${orgId}/datasets`,
  orgDatasetNew: (orgId: string) => `/org/${orgId}/datasets/new`,
  orgDatasetView: (orgId: string, datasetId: string) =>
    `/org/${orgId}/datasets/${datasetId}`,
  orgDataModels: (orgId: string) => `/org/${orgId}/data-models`,
  orgDataModelsNew: (orgId: string) => `/org/${orgId}/data-models/new`,
  orgDataModelsView: (orgId: string, id: string) =>
    `/org/${orgId}/data-models/${id}`,

  // Lakehouse routes - Organization level
  orgLakehouse: (orgId: string) => `/org/${orgId}/lakehouse`,
  orgLakehouseSources: (orgId: string) => `/org/${orgId}/lakehouse/sources`,
  orgLakehouseCatalog: (orgId: string) => `/org/${orgId}/lakehouse/catalog`,
  orgLakehouseCatalogUpload: (orgId: string) =>
    `/org/${orgId}/lakehouse/catalog/upload`,
  orgLakehouseTables: (orgId: string) => `/org/${orgId}/lakehouse/tables`,
  orgLakehouseTableNew: (orgId: string) => `/org/${orgId}/lakehouse/tables/new`,
  orgLakehouseTableView: (orgId: string, tableId: string) =>
    `/org/${orgId}/lakehouse/tables/${tableId}`,
  orgLakehouseIntegration: (orgId: string) => `/org/${orgId}/lakehouse/integration`,
  orgLakehouseIntegrationMigrate: (orgId: string) => `/org/${orgId}/lakehouse/integration/migrate`,
  orgLakehouseIntegrationDiscover: (orgId: string) => `/org/${orgId}/lakehouse/integration/discover`,
  orgLakehouseIntegrationEnhance: (orgId: string) => `/org/${orgId}/lakehouse/integration/enhance`,
  orgLakehouseIntegrationLineage: (orgId: string) => `/org/${orgId}/lakehouse/integration/lineage`,
  orgLakehouseSemantic: (orgId: string) => `/org/${orgId}/lakehouse/semantic`,
  orgLakehouseBusinessObjects: (orgId: string) =>
    `/org/${orgId}/lakehouse/semantic`,
  orgLakehouseSemanticObjects: (orgId: string) => `/org/${orgId}/lakehouse/semantic/objects`,
  orgLakehouseSemanticRelationships: (orgId: string) => `/org/${orgId}/lakehouse/semantic/relationships`,
  orgLakehouseSemanticDiscovery: (orgId: string) => `/org/${orgId}/lakehouse/semantic/discovery`,
  orgLakehouseSemanticBuilder: (orgId: string) => `/org/${orgId}/lakehouse/semantic/builder`,
  orgLakehouseSemanticView: (orgId: string, objectId: string) => `/org/${orgId}/lakehouse/semantic/${objectId}`,
  orgLakehouseQuery: (orgId: string) => `/org/${orgId}/lakehouse/query`,
  orgLakehouseQuerySQL: (orgId: string) => `/org/${orgId}/lakehouse/query/sql`,
  orgLakehouseQueryNatural: (orgId: string) => `/org/${orgId}/lakehouse/query/natural`,
  orgLakehouseQueryHistory: (orgId: string) => `/org/${orgId}/lakehouse/query/history`,
  orgLakehouseIngestion: (orgId: string) => `/org/${orgId}/lakehouse/ingestion`,
  orgLakehouseIngestionFiles: (orgId: string) => `/org/${orgId}/lakehouse/ingestion/files`,
  orgLakehouseIngestionDatabases: (orgId: string) => `/org/${orgId}/lakehouse/ingestion/databases`,
  orgLakehouseIngestionAPIs: (orgId: string) => `/org/${orgId}/lakehouse/ingestion/apis`,
  orgLakehouseUpload: (orgId: string) => `/org/${orgId}/lakehouse/ingestion/files`,
  orgLakehouseAppend: (orgId: string, tableName?: string) =>
    tableName
      ? `/org/${orgId}/lakehouse/ingestion/files?table=${tableName}`
      : `/org/${orgId}/lakehouse/ingestion/files`,
  orgLakehouseSchemaNew: (orgId: string) => `/org/${orgId}/lakehouse/schema/new`,
  orgLakehouseSchemaEdit: (orgId: string, tableId: string) =>
    `/org/${orgId}/lakehouse/schema/${tableId}`,
  orgLakehouseDataSpaces: (orgId: string) => `/org/${orgId}/lakehouse/data-spaces`,
  orgLakehouseDataSpaceNew: (orgId: string) => `/org/${orgId}/lakehouse/data-spaces/new`,
  orgLakehouseDataSpaceEdit: (orgId: string, dataSpaceId: string) => `/org/${orgId}/lakehouse/data-spaces/${dataSpaceId}/edit`,
  // Keep old namespace routes for backwards compatibility
  orgLakehouseNamespaces: (orgId: string) => `/org/${orgId}/lakehouse/data-spaces`,
  orgLakehouseNamespaceNew: (orgId: string) => `/org/${orgId}/lakehouse/data-spaces/new`,
  orgLakehouseNamespaceEdit: (orgId: string, namespaceId: string) => `/org/${orgId}/lakehouse/data-spaces/${namespaceId}/edit`,
  // Lakehouse Metrics routes
  orgLakehouseMetrics: (orgId: string) => `/org/${orgId}/lakehouse/metrics`,
  orgLakehouseMetricsNew: (orgId: string) => `/org/${orgId}/lakehouse/metrics/new`,
  orgLakehouseMetricsBuilder: (orgId: string) => `/org/${orgId}/lakehouse/metrics/builder`,
  orgLakehouseMetricsView: (orgId: string, metricId: string) => `/org/${orgId}/lakehouse/metrics/${metricId}`,

  // Lakehouse Views routes
  orgLakehouseViews: (orgId: string) => `/org/${orgId}/lakehouse/views`,
  orgLakehouseViewsNew: (orgId: string) => `/org/${orgId}/lakehouse/views/new`,
  orgLakehouseViewsView: (orgId: string, viewId: string) => `/org/${orgId}/lakehouse/views/${viewId}`,
  orgLakehouseViewsEdit: (orgId: string, viewId: string) => `/org/${orgId}/lakehouse/views/${viewId}/edit`,

  // Knowledge Bases routes
  orgKnowledgeBases: (orgId: string) => `/org/${orgId}/knowledge-bases`,
  orgKnowledgeBase: (orgId: string, kbId: string) => `/org/${orgId}/knowledge-bases/${kbId}`,
  orgKnowledgeBaseFolder: (orgId: string, kbId: string, folderId: string) =>
    `/org/${orgId}/knowledge-bases/${kbId}?folderId=${folderId}`,

  // Skills routes
  orgSkills: (orgId: string) => `/org/${orgId}/skills`,
  orgSkillsNew: (orgId: string) => `/org/${orgId}/skills/create`,
  orgSkillView: (orgId: string, skillId: string) => `/org/${orgId}/skills/${skillId}`,

  // Playbooks routes
  orgPlaybooks: (orgId: string) => `/org/${orgId}/playbooks`,
  orgPlaybooksNew: (orgId: string) => `/org/${orgId}/playbooks/new`,
  orgPlaybooksView: (orgId: string, playbookId: string) => `/org/${orgId}/playbooks/${playbookId}`,
  orgPlaybooksEdit: (orgId: string, playbookId: string) => `/org/${orgId}/playbooks/${playbookId}/edit`,
  orgPlaybooksRuns: (orgId: string, playbookId: string) => `/org/${orgId}/playbooks/${playbookId}/runs`,
  orgPlaybooksRunDetail: (orgId: string, playbookId: string, runId: string) => `/org/${orgId}/playbooks/${playbookId}/runs/${runId}`,
  orgPlaybooksRunStepDetail: (orgId: string, playbookId: string, runId: string, stepId: string) =>
    `/org/${orgId}/playbooks/${playbookId}/runs/${runId}/steps/${stepId}`,
  orgPlaybooksReports: (orgId: string, playbookId: string) => `/org/${orgId}/playbooks/${playbookId}/reports`,
  orgPlaybooksMonitor: (orgId: string) => `/org/${orgId}/playbooks/monitor`,
  orgPlaybooksRecords: (orgId: string) => `/org/${orgId}/playbooks/records`,
  orgPlaybooksPhaseRunners: (orgId: string) => `/org/${orgId}/playbooks/phase-runners`,
  orgPlaybookOrchestrations: (orgId: string) => `/org/${orgId}/playbooks/orchestrations`,
  orgPlaybookOrchestrationsNew: (orgId: string) => `/org/${orgId}/playbooks/orchestrations/new`,
  orgPlaybookOrchestrationView: (orgId: string, orchestratorId: string) =>
    `/org/${orgId}/playbooks/orchestrations/${orchestratorId}`,

  // Personal data routes
  dashboards: "/dashboards",
  dashboardsNew: "/dashboards/new",
  dashboardsView: (id: string) => `/dashboards/${id}`,
  datasets: "/datasets",
  datasetsNew: "/datasets/new",
  datasetsView: (id: string) => `/datasets/${id}`,
  dataModels: "/data-models",
  dataModelsNew: "/data-models/new",
  dataModelsView: (id: string) => `/data-models/${id}`,
};
